import React, { useState } from "react";
import { useAuth } from "providers/auth";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "api";
import { Features } from "providers/auth/types";
import { SuccessScreenView } from "views/rent-reporting-id-issues-m2m-lease-end/components/SuccessScreen";
import { format } from "date-fns";
import { NewLeaseEndDateView } from "./slices/new-lease-end-date";
import { LeaseWillBeM2MView } from "./slices/lease-m2m";
import { Loader } from "shared/components/Loader";
import { SelectScreenView } from "views/rent-reporting-id-issues-m2m-lease-end/components/SelectScreenView";
import {
  SelectOption,
  View
} from "views/rent-reporting-id-issues-m2m-lease-end/types";
import { Redirect } from "common/Redirect";

const handleContinue = (
  selectedOption: SelectOption,
  setView: (view: View) => void
) => {
  if (selectedOption === SelectOption.LeaseIsEnding) setView(View.SuccessView);

  if (selectedOption === SelectOption.NewLeaseEndDate)
    setView(View.LeaseDetailsView);

  if (selectedOption === SelectOption.LeaseWillBeM2M)
    setView(View.LeaseWillBeM2MView);
};

export const LeaseEndingIssueView = () => {
  const { partner } = useAuth();
  const { id } = useParams();

  const navigate = useNavigate();
  const {
    data: enrollment,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["CURRENT_ENROLLMENT", id],
    queryFn: () =>
      api.getDashboard().then((res) => {
        return res.data.enrollments.find(
          (enrollment) => enrollment.id === Number(id)
        );
      }),
    enabled: Boolean(
      partner?.features?.includes(Features.ENABLE_SDK_RENT_REPORTING)
    )
  });
  const [view, setView] = useState<View>(View.SelectView);
  const [selectedOption, setSelectedOption] = useState<SelectOption>(
    SelectOption.LeaseIsEnding
  );

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  if (isError || !enrollment)
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-content-1000 font-medium text-base">
          Error loading enrollment data
        </p>
      </div>
    );

  const leaseEndDate = enrollment.enrollment_case?.lease_end_date
    ? format(enrollment.enrollment_case?.lease_end_date, "MMMM dd, yyyy")
    : "";

  if (view === View.SelectView) {
    const optionsMap = [
      {
        id: SelectOption.LeaseIsEnding,
        title: `Ending my lease on on ${leaseEndDate}`
      },
      {
        id: SelectOption.NewLeaseEndDate,
        title: "I have a new lease end date"
      },
      {
        id: SelectOption.LeaseWillBeM2M,
        title: "Lease will be month-to-month"
      }
    ];
    return (
      <SelectScreenView
        handleContinue={() => handleContinue(selectedOption, setView)}
        subtitle="What best describes your lease?"
        title={`Your lease is expected to end on ${leaseEndDate}`}
        options={optionsMap}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />
    );
  }
  if (view === View.SuccessView)
    return (
      <SuccessScreenView
        title="Great!"
        subtext1={`On ${leaseEndDate} we will complete your reporting and close your tradeline, as long as all payments are verified.`}
        subtext2="You can add your next rental address for reporting from the home screen"
        buttonTitle="Done"
        onButtonClick={() => navigate("/rent_reporting/issues/verify-rent")}
      />
    );

  if (view === View.LeaseDetailsView)
    return (
      <NewLeaseEndDateView
        leaseEndDate={leaseEndDate}
        rentAmount={enrollment.monthly_rent_amount}
        setView={setView}
      />
    );

  if (view === View.LeaseWillBeM2MView)
    return (
      <LeaseWillBeM2MView
        leaseEndDate={leaseEndDate}
        setView={setView}
        rentAmount={enrollment.monthly_rent_amount}
      />
    );

  return <Redirect href="/hub?with_intercom=true" />;
};
